import React, { useState } from "react";
import { Skeleton } from "@mui/material";
import { toastr } from "react-redux-toastr";
import ClipLoader from "react-spinners/ClipLoader";
import { Box, Grid, useMediaQuery } from "@material-ui/core";
import DeleteSvgComponent from "assets/icons/svg/DeleteSvg/Delete";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
// component
import AlertDialog from "components/Dialog";
import FinanceService from "Lib/API/Finance";
import Button from "components/button/Button";
import Stripe from "components/StripeCard/Stripe";
import { InputField, MyCheckbox } from "components";
import BackSvgComponent from "assets/icons/svg/back";
import { getAccessTokenSilently } from "utils/functions";
import { postalCodeLabel } from "helper/postalCodeFormat";
import { CreditCardMethodItem } from "pages/Settings/Theme";
import { CreditCardMethodButton } from "pages/Settings/Theme";
import { CreditCardMethodContent } from "pages/Settings/Theme";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
// direct import
import { PaymentButton } from "./Theme";
import { Fonts, FontColors } from "theme/fontConfig";

const CreditCardList = ({
  formik,
  isStripe,
  setSelectedCard,
  selectedCard,
  publicKey,
  cardList,
  addCardStripeRef,
  cardLoading,
  getCreditCards,
  setAddNewCard,
  addNewCard,
}) => {
  const postalLabel = postalCodeLabel();
  const isSmallScreen = useMediaQuery("(max-width:750px)");

  const [creditId, setCreditId] = useState(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [addCardLoading, setAddCardLoading] = useState(false);
  const [setDefaultLoading, setSetDefaultLoading] = useState(false);

  const openConfirmDialog = (id) => {
    setCreditId(id);
    setOpenAlert(true);
  };

  const closeConfirmDialog = () => {
    setOpenAlert(false);
    setCreditId(null);
  };

  const handleSetDefaultCard = async (cardId) => {
    const token = await getAccessTokenSilently();
    const accountID = localStorage.getItem("accountID");
    const data = {
      accountId:
        formik?.values?.accountInfo?.main_contact?.account_id ?? accountID,
      cardId,
    };
    setSetDefaultLoading(true);
    await FinanceService.setDefaultCreditCard(data, token)
      .then((res) => {
        toastr.success("Success", "Default card set successfully");
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setSetDefaultLoading(false);
      });
  };

  const handleDefaultCardChange = (cardId) => {
    setSelectedCard(cardId);
    handleSetDefaultCard(cardId);
  };

  const handleDelete = async (cardID) => {
    const token = await getAccessTokenSilently();
    const accountID = localStorage.getItem("accountID");
    const accountInfo = formik?.values?.accountInfo;

    FinanceService.deleteCreditCard(
      token,
      cardID,
      accountInfo?.main_contact?.account_id ?? accountID
    )
      .then((res) => {
        if (res?.card?.messages?.resultCode === "Ok") {
          toastr.success("Success", "Card has been delete successfully ");
        }
        getCreditCards(accountInfo);
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message);
      })
      .finally(() => {
        closeConfirmDialog();
      });
  };

  return (
    <div className="card_num_field">
      <CreditCardMethodContent>
        {cardLoading || setDefaultLoading ? (
          <Box>
            {[1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  boxShadow: 1,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width={60}
                    height={40}
                    sx={{ borderRadius: 1 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton
                      variant="text"
                      height={22}
                      sx={{ width: "70%" }}
                    />
                    <Skeleton
                      variant="text"
                      height={18}
                      sx={{ width: "45%", mt: 0.5 }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <>
            {!!cardList?.length && (
              <div
                className="cards-radio font-16 d-flex flex-column items-center "
                style={{
                  gap: "10px",
                }}
              >
                {cardList?.map((card, index) => {
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        width: "100%",
                        flexDirection: isSmallScreen ? "column" : "row",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        gap: isSmallScreen ? 0 : 20,
                      }}
                    >
                      <CreditCardMethodItem>
                        {`${card.country ? card.country : ""} ${
                          card.brand ? card.brand : ""
                        } (${card.last4 ? card.last4 : ""}), Exp: ${
                          card.exp_month
                        }/${card.exp_year}`}
                        <CreditCardMethodButton
                          onClick={() => openConfirmDialog(card?.id)}
                        >
                          <DeleteSvgComponent color={"gray"} />
                        </CreditCardMethodButton>
                      </CreditCardMethodItem>

                      <MyCheckbox
                        title={"Set as default"}
                        handlechange={(e) => handleDefaultCardChange(card?.id)}
                        checked={selectedCard === card?.id}
                        style={{
                          marginTop: isSmallScreen ? 10 : 0,
                        }}
                      />
                    </Box>
                  );
                })}
              </div>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <div
                className="card__header"
                style={{
                  width: isSmallScreen ? "100%" : "auto",
                  marginTop: 30,
                }}
              >
                {!addNewCard && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => {
                      const newValue = !addNewCard;
                      setAddNewCard(newValue);
                      formik.setFieldValue("addCard", newValue);
                    }}
                    style={{
                      minWidth: 165,
                    }}
                  >
                    <PlusIcon />
                    <span>Add New Card</span>
                  </Button>
                )}
                {addNewCard && (
                  <div
                    className="cancel-add-button"
                    style={{ padding: "8px 0px" }}
                  >
                    <BackSvgComponent
                      onClick={() => {
                        setAddNewCard(false);
                        formik.setFieldValue("addCard", false);
                        formik.setFieldValue("cardsDetails", {
                          card_number: null,
                          cvc: null,
                          expMonth: null,
                          expYear: null,
                        });
                      }}
                      role="button"
                    />
                  </div>
                )}
              </div>
            </Box>

            {addNewCard && (
              <>
                {isStripe ? (
                  <Grid item xs={12}>
                    <Stripe
                      loading={addCardLoading}
                      setLoading={setAddCardLoading}
                      saveCard={false}
                      ref={addCardStripeRef}
                      publicKey={publicKey}
                    />
                  </Grid>
                ) : (
                  <div className="row">
                    <div className="col-md-6">
                      <InputField
                        mandatory
                        {...{
                          title: "Card Number",
                          name: "card_number",
                          type: "string",
                          maxLength: 16,
                          minlength: 16,
                          pattern: "[0-9]{10}",
                          placeholderIcon: (
                            <CreditCardOutlinedIcon
                              className="card-icon"
                              fontSize="large"
                            />
                          ),
                        }}
                        id="myInput"
                        value={formik?.values?.cardsDetails?.card_number}
                        onKeyPress={(event) => {
                          if (!/[0-9]/.test(event.key)) {
                            event.preventDefault();
                          }
                        }}
                        handleChange={(e) => {
                          formik.setFieldValue(
                            "cardsDetails.card_number",
                            e?.target?.value
                          );
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        mandatory
                        {...{
                          title: "Expiry Month",
                          name: `expMonth`,
                          placeholder: "MM",
                          minlength: "1",
                          maxLength: "2",
                          type: "string",
                        }}
                        onKeyPress={(event) => {
                          if (!/[0-9]/.test(event.key)) {
                            event.preventDefault();
                          }
                        }}
                        value={formik?.values?.cardsDetails?.expMonth}
                        handleChange={(e) => {
                          formik.setFieldValue(
                            "cardsDetails.expMonth",
                            e?.target?.value
                          );
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <InputField
                        mandatory
                        {...{
                          title: "Expiry Year",
                          type: "string",
                          minlength: "4",
                          maxLength: "4",
                          name: `expYear`,
                          placeholder: "YYYY",
                        }}
                        onKeyPress={(event) => {
                          if (!/[0-9]/.test(event.key)) {
                            event.preventDefault();
                          }
                        }}
                        value={formik?.values?.cardsDetails?.expYear}
                        handleChange={(e) => {
                          formik.setFieldValue(
                            "cardsDetails.expYear",
                            e?.target?.value
                          );
                        }}
                      />
                    </div>
                    <div className="col-sm-4">
                      <InputField
                        mandatory
                        {...{
                          title: "CVC",
                          type: "string",
                          minlength: "3",
                          maxLength: "4",
                          name: `cvc`,
                          placeholder: "3-4 digits",
                        }}
                        value={formik?.values?.cardsDetails?.cvc}
                        onKeyPress={(event) => {
                          if (!/[0-9]/.test(event.key)) {
                            event.preventDefault();
                          }
                        }}
                        handleChange={(e) => {
                          formik.setFieldValue(
                            "cardsDetails.cvc",
                            e?.target?.value
                          );
                        }}
                      />
                    </div>
                    <div className="col-sm-4">
                      <InputField
                        mandatory
                        {...{
                          title: postalLabel,
                          type: "string",
                          name: `zip`,
                          placeholder: "12345",
                        }}
                        value={formik?.values?.cardsDetails?.zip}
                        handleChange={(e) => {
                          formik.setFieldValue(
                            "cardsDetails.zip",
                            e?.target?.value
                          );
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
        {formik?.values?.addCardError && (
          <Grid item xs={12}>
            <Box
              sx={{
                color: FontColors.Red,
                position: "relative",
                top: -13,
              }}
            >
              {formik?.values?.addCardError}
            </Box>
          </Grid>
        )}
      </CreditCardMethodContent>
      {addNewCard && (
        <Grid item xs={12} className="d-flex align-items-end">
          <PaymentButton>
            <button
              onClick={formik.handleSubmit}
              type="button"
              disabled={formik?.values?.cardLoading}
              style={{
                opacity: formik?.values?.cardLoading ? 0.5 : 1,
              }}
            >
              {formik?.values?.cardLoading ? (
                <ClipLoader
                  color={"#ffffff"}
                  loading={formik?.values?.cardLoading}
                  size={22}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />
              ) : (
                "Add Payment Method"
              )}
            </button>
          </PaymentButton>
        </Grid>
      )}

      <AlertDialog
        open={openAlert}
        handleClose={() => {
          closeConfirmDialog();
        }}
        onDecline={() => {
          setOpenAlert(false);
        }}
        onAccept={() => {
          handleDelete(creditId);
        }}
        content={"Are you sure you want to delete this credit card?"}
        contentStyle={{
          fontSize: Fonts.GeneralXLargeBodyFont.fontSize,
        }}
      />
    </div>
  );
};

export default CreditCardList;
